import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { BuildsTable } from '@/features/builds/components/BuildsTable';
import { queryBuilds } from '@/features/builds/data/builds';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { PipelinesTable } from '@/features/pipelines/components/PipelinesTable';
import { queryPipelines } from '@/features/pipelines/data';
import {
  pipelineDefaultColumnVisibilityModel,
  pipelineDefaultSortDirection,
  pipelineDefaultSortKey,
  type PipelineSortDirection,
  pipelineSortFieldMap,
  type PipelineSortKey,
  type PipelineTableStatus,
} from '@/features/pipelines/pages/pipelinesTableDefinition';
import {
  type CloneDateQuickFilter,
  PipelinesTableToolbar,
} from '@/features/pipelines/pages/PipelinesTableToolbar';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { Box, type GridColumnVisibilityModel, Tab, Tabs } from '@/ui/primitives';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { BuildDto, BuildStatus, PipelineListItemDto, QueryResult } from '@flowqa/shared';

type PipelinesPanelTab = 'pipelines' | 'builds';
type BuildSortDirection = 'asc' | 'desc';
type BuildSortKey = 'startedAt' | 'finishedAt' | 'status' | 'pipelineName' | 'buildKey';

const buildSortFieldMap: Record<BuildSortKey, string> = {
  startedAt: 'startedAt',
  finishedAt: 'finishedAt',
  status: 'status',
  pipelineName: 'pipelineName',
  buildKey: 'buildKey',
};

const buildSortOptions: Array<{ key: BuildSortKey; label: string }> = [
  { key: 'startedAt', label: 'Started' },
  { key: 'finishedAt', label: 'Finished' },
  { key: 'status', label: 'Status' },
  { key: 'pipelineName', label: 'Pipeline' },
  { key: 'buildKey', label: 'Run' },
];

const buildColumnOptions: Array<{ field: string; label: string; mandatory?: boolean }> = [
  { field: 'pipelineName', label: 'Pipeline' },
  { field: 'buildKey', label: 'Run', mandatory: true },
  { field: 'status', label: 'Status' },
];

const buildDefaultColumnVisibilityModel: GridColumnVisibilityModel = {
  pipelineName: true,
  buildKey: true,
  status: true,
};

const buildDefaultSortKey: BuildSortKey = 'startedAt';
const buildDefaultSortDirection: BuildSortDirection = 'desc';

export type PipelinesTablePanelProps = {
  activeTab: PipelinesPanelTab;
  onActiveTabChange: (nextTab: PipelinesPanelTab) => void;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  deltasEnabled: boolean;
  pipelineSearchValue: string;
  onPipelineSearchChange: (value: string) => void;
  pipelineFilterStatuses: PipelineTableStatus[];
  onPipelineFilterStatusesChange: (next: PipelineTableStatus[]) => void;
  pipelineFilterTags: string[];
  onPipelineFilterTagsChange: (next: string[]) => void;
  pipelineDateQuickFilter: CloneDateQuickFilter;
  onPipelineDateQuickFilterChange: (next: CloneDateQuickFilter) => void;
  pipelineFromDate: Dayjs | null;
  pipelineToDate: Dayjs | null;
  onPipelineFromDateChange: (next: Dayjs | null) => void;
  onPipelineToDateChange: (next: Dayjs | null) => void;
};

export const PipelinesTablePanel = ({
  activeTab,
  onActiveTabChange,
  selectedProjectId,
  selectedOrganisationId,
  deltasEnabled,
  pipelineSearchValue,
  onPipelineSearchChange,
  pipelineFilterStatuses,
  onPipelineFilterStatusesChange,
  pipelineFilterTags,
  onPipelineFilterTagsChange,
  pipelineDateQuickFilter,
  onPipelineDateQuickFilterChange,
  pipelineFromDate,
  pipelineToDate,
  onPipelineFromDateChange,
  onPipelineToDateChange,
}: PipelinesTablePanelProps): ReactElement => {
  const [pipelineColumnVisibilityModel, setPipelineColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(pipelineDefaultColumnVisibilityModel);
  const [pipelineSortKey, setPipelineSortKey] = useState<PipelineSortKey>(pipelineDefaultSortKey);
  const [pipelineSortDirection, setPipelineSortDirection] = useState<PipelineSortDirection>(
    pipelineDefaultSortDirection,
  );
  const [pipelineMeta, setPipelineMeta] = useState<TableQueryMeta<PipelineListItemDto> | null>(
    null,
  );
  const [debouncedBuildSearch, setDebouncedBuildSearch] = useState('');
  const [buildDateQuickFilter, setBuildDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [buildFromDate, setBuildFromDate] = useState<Dayjs | null>(null);
  const [buildToDate, setBuildToDate] = useState<Dayjs | null>(null);
  const [buildColumnVisibilityModel, setBuildColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(buildDefaultColumnVisibilityModel);
  const [buildSortKey, setBuildSortKey] = useState<BuildSortKey>(buildDefaultSortKey);
  const [buildSortDirection, setBuildSortDirection] =
    useState<BuildSortDirection>(buildDefaultSortDirection);
  const [buildFilterStatuses, setBuildFilterStatuses] = useState<BuildStatus[]>([]);
  const [buildFilterTags, setBuildFilterTags] = useState<string[]>([]);
  const [buildMeta, setBuildMeta] = useState<TableQueryMeta<BuildDto> | null>(null);

  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const availableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const pipelineDateWindow = useCloneDateWindow({
    dateQuickFilter: pipelineDateQuickFilter,
    fromDate: pipelineFromDate,
    toDate: pipelineToDate,
  });
  const buildDateWindow = useCloneDateWindow({
    dateQuickFilter: buildDateQuickFilter,
    fromDate: buildFromDate,
    toDate: buildToDate,
  });

  const handleBuildDateQuickFilterChange = useCallback((next: CloneDateQuickFilter) => {
    setBuildDateQuickFilter(next);
    setBuildFromDate(null);
    setBuildToDate(null);
  }, []);

  const pipelineBuildStatusFilters = useMemo<GridQueryInput['filters']>(() => {
    if (pipelineFilterStatuses.length === 0) return [];
    const hasNoBuilds = pipelineFilterStatuses.includes('none');
    const buildStatuses = pipelineFilterStatuses.filter(
      (status): status is Exclude<PipelineTableStatus, 'none'> => status !== 'none',
    );
    const values = hasNoBuilds
      ? ([null, ...buildStatuses] as Array<Exclude<PipelineTableStatus, 'none'> | null>)
      : buildStatuses;
    return buildInFilterWithNull('latestBuildStatus', values);
  }, [pipelineFilterStatuses]);

  const pipelineQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<PipelineListItemDto>> => {
      const toolbarFilters = mergeFilters(
        pipelineBuildStatusFilters,
        buildInFilter('tags', pipelineFilterTags),
      );
      const sortOverride: ApiSort = {
        field: pipelineSortFieldMap[pipelineSortKey],
        direction: pipelineSortDirection,
      };
      return queryPipelines(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        sorts: [sortOverride],
        filters: [...input.filters, ...toolbarFilters],
        metricsStartDay: pipelineDateWindow.startDay ?? undefined,
        metricsEndDay: pipelineDateWindow.endDay ?? undefined,
        previousMetricsStartDay: pipelineDateWindow.previousStartDay ?? undefined,
        previousMetricsEndDay: pipelineDateWindow.previousEndDay ?? undefined,
        include: {
          latestBuild: true,
          metrics: true,
          metricDeltas: deltasEnabled,
        },
      });
    },
    [
      deltasEnabled,
      pipelineDateWindow.endDay,
      pipelineDateWindow.previousEndDay,
      pipelineDateWindow.previousStartDay,
      pipelineDateWindow.startDay,
      pipelineBuildStatusFilters,
      pipelineFilterTags,
      pipelineSortDirection,
      pipelineSortKey,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );

  const buildQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<BuildDto>> => {
      const toolbarFilters = mergeFilters(
        buildContainsFilter('pipelineName', debouncedBuildSearch),
        buildInFilter('status', buildFilterStatuses),
        buildInFilter('tags', buildFilterTags),
        buildDateRangeFilters(
          'startedAt',
          buildDateWindow.startDay
            ? dayjs(buildDateWindow.startDay).startOf('day').toISOString()
            : null,
          buildDateWindow.endDay ? dayjs(buildDateWindow.endDay).endOf('day').toISOString() : null,
        ),
      );
      const sortOverride: ApiSort = {
        field: buildSortFieldMap[buildSortKey],
        direction: buildSortDirection,
      };
      return queryBuilds(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        sorts: [sortOverride],
        filters: [...input.filters, ...toolbarFilters],
      });
    },
    [
      buildFilterStatuses,
      buildFilterTags,
      buildSortDirection,
      buildSortKey,
      buildDateWindow.endDay,
      buildDateWindow.startDay,
      debouncedBuildSearch,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );

  const pipelineNoRowsText = pipelineDateWindow.hasCustomBounds
    ? 'No pipelines found for selected date range'
    : formatRangeWindowNoDataText('No pipelines found', pipelineDateQuickFilter, false);
  const buildNoRowsText = buildDateWindow.hasCustomBounds
    ? 'No builds found for selected date range'
    : formatRangeWindowNoDataText('No builds found', buildDateQuickFilter, false);
  const pipelineStatusFilterKey = useMemo(
    () => [...pipelineFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [pipelineFilterStatuses],
  );
  const pipelineTagFilterKey = useMemo(
    () => [...pipelineFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [pipelineFilterTags],
  );
  const buildStatusFilterKey = useMemo(
    () => [...buildFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [buildFilterStatuses],
  );
  const buildTagFilterKey = useMemo(
    () => [...buildFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [buildFilterTags],
  );

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={activeTab}
                onChange={(_event, next) => {
                  onActiveTabChange(next as PipelinesPanelTab);
                }}
                aria-label="Pipelines table tabs"
                sx={{ pl: s('sm') }}
              >
                <Tab label="Pipelines" value="pipelines" />
                <Tab label="Builds" value="builds" />
              </Tabs>
            </Box>
            {activeTab === 'pipelines' ? (
              <Box sx={{ flex: '0 0 auto' }}>
                <PipelinesTableToolbar
                  onDebouncedSearchChange={onPipelineSearchChange}
                  columnVisibilityModel={pipelineColumnVisibilityModel}
                  onColumnVisibilityModelChange={setPipelineColumnVisibilityModel}
                  sortKey={pipelineSortKey}
                  sortDirection={pipelineSortDirection}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setPipelineSortKey(nextSortKey);
                    setPipelineSortDirection(nextSortDirection);
                  }}
                  filterStatuses={pipelineFilterStatuses}
                  onFilterStatusesChange={onPipelineFilterStatusesChange}
                  filterTags={pipelineFilterTags}
                  onFilterTagsChange={onPipelineFilterTagsChange}
                  dateQuickFilter={pipelineDateQuickFilter}
                  onDateQuickFilterChange={onPipelineDateQuickFilterChange}
                  fromDate={pipelineFromDate}
                  toDate={pipelineToDate}
                  onFromDateChange={onPipelineFromDateChange}
                  onToDateChange={onPipelineToDateChange}
                  availableTags={availableTags}
                  summaryCount={pipelineMeta?.total ?? null}
                />
              </Box>
            ) : (
              <Box sx={{ flex: '0 0 auto' }}>
                <TableToolbar<BuildSortKey, BuildStatus>
                  title={undefined}
                  searchAriaLabel="Search builds by pipeline"
                  onDebouncedSearchChange={setDebouncedBuildSearch}
                  columnVisibilityModel={buildColumnVisibilityModel}
                  defaultColumnVisibilityModel={buildDefaultColumnVisibilityModel}
                  columnOptions={buildColumnOptions}
                  onColumnVisibilityModelChange={setBuildColumnVisibilityModel}
                  sortKey={buildSortKey}
                  sortDirection={buildSortDirection}
                  defaultSortKey={buildDefaultSortKey}
                  defaultSortDirection={buildDefaultSortDirection}
                  sortOptions={buildSortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setBuildSortKey(nextSortKey);
                    setBuildSortDirection(nextSortDirection);
                  }}
                  filterStatuses={buildFilterStatuses}
                  statusOptions={buildStatusOptions}
                  onFilterStatusesChange={setBuildFilterStatuses}
                  filterTags={buildFilterTags}
                  onFilterTagsChange={setBuildFilterTags}
                  dateQuickFilter={buildDateQuickFilter}
                  onDateQuickFilterChange={handleBuildDateQuickFilterChange}
                  fromDate={buildFromDate}
                  toDate={buildToDate}
                  onFromDateChange={setBuildFromDate}
                  onToDateChange={setBuildToDate}
                  availableTags={availableTags}
                  summaryCount={buildMeta?.total ?? null}
                  summarySingularLabel="build"
                  summaryPluralLabel="builds"
                />
              </Box>
            )}
          </Stack>
        </Box>
      }
      sx={{ backgroundColor: 'background.paper' }}
    >
      {activeTab === 'pipelines' ? (
        <PipelinesTable
          persistKey={null}
          queryKey={[
            'pipelines-table',
            pipelineDateQuickFilter,
            selectedOrganisationId ?? null,
            selectedProjectId ?? null,
            deltasEnabled ? 'deltas-on' : 'deltas-off',
            pipelineSearchValue,
            pipelineSortKey,
            pipelineSortDirection,
            pipelineStatusFilterKey,
            pipelineTagFilterKey,
            pipelineDateWindow.startDay,
            pipelineDateWindow.endDay,
          ]}
          queryFn={pipelineQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          showDeltas={deltasEnabled}
          noRowsText={pipelineNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={pipelineSearchValue}
          onQueryMeta={setPipelineMeta}
          columnVisibilityModel={pipelineColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setPipelineColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      ) : (
        <BuildsTable
          persistKey={null}
          queryKey={[
            'builds-table',
            buildDateQuickFilter,
            selectedOrganisationId ?? null,
            selectedProjectId ?? null,
            buildSortKey,
            buildSortDirection,
            buildStatusFilterKey,
            buildTagFilterKey,
            buildDateWindow.startDay,
            buildDateWindow.endDay,
          ]}
          queryFn={buildQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          noRowsText={buildNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={debouncedBuildSearch}
          onQueryMeta={setBuildMeta}
          columnVisibilityModel={buildColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setBuildColumnVisibilityModel({
              ...nextModel,
              buildKey: true,
            });
          }}
        />
      )}
    </Panel>
  );
};
